/**
 * Roles Management Hooks
 * React hooks for managing roles and permissions
 */

import { useMemo, useEffect } from 'react';
import { useMutation, useQuery } from './useBaseHooks';
import { adminApiClient } from './adminApiClient';
import { Role, Permission, RoleDTO } from './adminTypes';
import { ApiResponse, Pageable, PagedData } from './types';
import { MOCK_PERMISSIONS } from '@/lib/permissions';

/**
 * Hook to get roles dropdown list
 * Used for role selection in user forms
 */
export function useRolesDropdown(
  options: {
    enabled?: boolean;
    onSuccess?: (data: Role[]) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const { enabled = true, onSuccess, onError } = options;

  // Normalize each item to a consistent { roleId, roleName } shape
  const normalizeRole = (item: any): Role => {
    if (typeof item === 'string') {
      return { roleId: item, roleName: item } as Role;
    }
    return {
      ...item,
      roleId: item.roleId ?? item.id ?? item.role_id ?? item.roleName ?? item.name,
      roleName: item.roleName ?? item.name ?? item.role_name ?? item.roleId ?? item.id,
    } as Role;
  };

  // Memoize the query function to prevent infinite re-renders
  const queryFn = useMemo(
    () => async () => {
      const response = await adminApiClient.get<ApiResponse<Role[]>>(
        '/roles/filtered-list'
      );

      let raw: any[] = [];

      // Handle Response wrapper - API returns { status, data: { content: Role[], page: {...} } }
      if (response && response.data && response.data.content && Array.isArray(response.data.content)) {
        raw = response.data.content;
      } else if (response && response.data && Array.isArray(response.data)) {
        raw = response.data;
      } else if (response && Array.isArray(response)) {
        raw = response;
      }

      return raw.map(normalizeRole);
    },
    []
  );

  const query = useQuery<Role[]>(
    queryFn,
    {
      enabled,
      onSuccess: (data) => {
        onSuccess?.(data);
      },
      onError: (error) => {
        onError?.(error);
      },
    }
  );

  return query;
}

/**
 * Hook to get all roles with pagination
 */
export function useRoles(
  options: {
    search?: string;
    pageable?: Pageable;
    enabled?: boolean;
    onSuccess?: (data: PagedData<Role>) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const { search, pageable, enabled = true, onSuccess, onError } = options;

  // Memoize the query function to prevent infinite re-renders
  const queryFn = useMemo(
    () => async () => {
      const params: Record<string, any> = {};
      
      if (search) {
        params.search = search;
      }
      
      if (pageable) {
        params.page = pageable.page ?? 0;
        params.size = pageable.size ?? 10;
        if (pageable.sort && pageable.sort.length > 0) {
          params.sort = pageable.sort;
        }
      } else {
        params.page = 0;
        params.size = 10;
      }

      const response = await adminApiClient.get<ApiResponse<Role[]>>(
        '/roles/list',
        params
      );

      // Handle Response wrapper - API returns { status, httpStatus, message, data: Role[] }
      if (response && response.data && Array.isArray(response.data)) {
        // Convert array to PagedData format
        const roles = response.data;
        return {
          content: roles,
          totalElements: roles.length,
          totalPages: 1,
          size: roles.length,
          number: 0,
          first: true,
          last: true,
          empty: roles.length === 0,
        };
      }

      // Handle case where response is directly the array
      if (response && Array.isArray(response)) {
        const roles = response;
        return {
          content: roles,
          totalElements: roles.length,
          totalPages: 1,
          size: roles.length,
          number: 0,
          first: true,
          last: true,
          empty: roles.length === 0,
        };
      }

      // If response.data is already PagedData
      if (response && response.data && typeof response.data === 'object' && 'content' in response.data) {
        return response.data as unknown as PagedData<Role>;
      }

      // Handle case where response itself is PagedData
      if (response && typeof response === 'object' && 'content' in response && Array.isArray(response.content)) {
        return response as any;
      }
      // Fallback: return empty PagedData
      return {
        content: [],
        totalElements: 0,
        totalPages: 0,
        size: 0,
        number: 0,
        first: true,
        last: true,
        empty: true,
      };
    },
    [search, pageable?.page, pageable?.size, JSON.stringify(pageable?.sort || [])]
  );

  const query = useQuery<PagedData<Role>>(
    queryFn,
    {
      enabled,
      onSuccess: (data) => {
        onSuccess?.(data);
      },
      onError: (error) => {
        onError?.(error);
      },
    }
  );

  return query;
}

/**
 * Hook to get a single role by ID
 */
export function useRole(
  roleId: number | string,
  options: {
    enabled?: boolean;
    onSuccess?: (data: Role) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const { enabled = true, onSuccess, onError } = options;

  // Memoize the query function
  const queryFn = useMemo(
    () => async () => {
      const response = await adminApiClient.get<ApiResponse<Role>>(
        `/roles/${roleId}`
      );
      
      // Handle Response wrapper
      if (response.data) {
        return response.data;
      }
      
      return response as any;
    },
    [roleId]
  );

  const query = useQuery<Role>(
    queryFn,
    {
      enabled: enabled && !!roleId,
      onSuccess: (data) => {
        onSuccess?.(data);
      },
      onError: (error) => {
        onError?.(error);
      },
    }
  );

  return query;
}

/**
 * Hook to create a new role
 */
export function useCreateRole(
  options: {
    onSuccess?: (data: ApiResponse<Role>, variables: RoleDTO) => void;
    onError?: (error: Error, variables: RoleDTO) => void;
  } = {}
) {
  const { onSuccess, onError } = options;

  const mutation = useMutation<ApiResponse<Role>, RoleDTO>(
    async (roleData: RoleDTO) => {
      const response = await adminApiClient.post<ApiResponse<Role>>(
        '/roles',
        roleData
      );
      return response;
    },
    {
      onSuccess: (data, variables) => {
        onSuccess?.(data, variables);
      },
      onError: (error, variables) => {
        onError?.(error, variables);
      },
    }
  );

  return mutation;
}

/**
 * Hook to update an existing role
 */
export function useUpdateRole(
  options: {
    onSuccess?: (data: ApiResponse<Role>, variables: RoleDTO & { id: number }) => void;
    onError?: (error: Error, variables: RoleDTO & { id: number }) => void;
  } = {}
) {
  const { onSuccess, onError } = options;

  const mutation = useMutation<ApiResponse<Role>, RoleDTO & { id: number }>(
    async (roleData: RoleDTO & { id: number }) => {
      const { id, ...updateData } = roleData;
      const response = await adminApiClient.put<ApiResponse<Role>>(
        `/roles/${id}`,
        updateData
      );
      return response;
    },
    {
      onSuccess: (data, variables) => {
        onSuccess?.(data, variables);
      },
      onError: (error, variables) => {
        onError?.(error, variables);
      },
    }
  );

  return mutation;
}

/**
 * Hook to delete a role
 */
export function useDeleteRole(
  options: {
    onSuccess?: (data: ApiResponse<void>, variables: number) => void;
    onError?: (error: Error, variables: number) => void;
  } = {}
) {
  const { onSuccess, onError } = options;

  const mutation = useMutation<ApiResponse<void>, number>(
    async (roleId: number) => {
      const response = await adminApiClient.delete<ApiResponse<void>>(
        `/roles/${roleId}`
      );
      return response;
    },
    {
      onSuccess: (data, variables) => {
        onSuccess?.(data, variables);
      },
      onError: (error, variables) => {
        onError?.(error, variables);
      },
    }
  );

  return mutation;
}

/**
 * Hook to get all permissions
 * Note: Using hardcoded permissions from shared permissions file
 */
export function usePermissions(
  options: {
    enabled?: boolean;
    onSuccess?: (data: Permission[]) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const { enabled = true, onSuccess, onError } = options;

  // Simulate async behavior to match the expected hook interface
  useEffect(() => {
    if (!enabled) return;

    // Simulate a small delay
    const timer = setTimeout(() => {
      onSuccess?.(MOCK_PERMISSIONS);
    }, 0);

    return () => clearTimeout(timer);
  }, [enabled, onSuccess]);

  return {
    data: MOCK_PERMISSIONS,
    loading: false,
    error: null,
    refetch: async () => {
      // No-op since permissions are hardcoded
    },
  };
}

